import { db } from '@/services/firebase';
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  addDoc,
  Timestamp,
} from 'firebase/firestore';
import { Directive } from '../directive/DirectiveTypes';
import { isFirebaseE2EMockEnabled } from '@/utils/e2eMode';
import { RouterCallContext } from './a2a/transport/A2ATransport';
import { importWithRetry } from '@/utils/dynamicImport';


export type PlanShape = 'atomic' | 'workflow' | 'timeline';
export type PlanStatus = 'drafting' | 'awaiting_approval' | 'executing' | 'proposed' | 'completed' | 'failed' | 'cancelled';

export interface PlanStep {
  id: string;
  title: string;
  description: string;
  toolName?: string;
  input?: Record<string, unknown>;
  status: 'pending' | 'executing' | 'complete' | 'error';
  error?: string;
  result?: unknown;
  dependsOn?: string[];
  agentId?: string;
}

export interface Phase {
  id: string;
  title: string;
  days: number;
  milestones: string[];
}

export interface PlanDraft {
  shape: PlanShape;
  summary: string;
  steps?: PlanStep[];
  phases?: Phase[];
  durationDays?: number;
  estimatedCost?: {
    tokens: number;
    dollars: number;
  };
  risks?: string[];
  autoApprove: boolean;
}

export interface Revision {
  timestamp: Timestamp;
  userMessage: string;
  agentResponse: string;
  draftBefore: PlanDraft;
  draftAfter: PlanDraft;
}

export interface LivingPlan {
  id: string;
  projectId: string;
  userId: string;
  goal: string;
  draft: PlanDraft;
  history: Revision[];
  status: PlanStatus;
  approvedAt?: Timestamp;
  executionRef?: {
    kind: 'atomic' | 'workflow' | 'timeline';
    id: string;
  };
  createdAt: Timestamp;
  updatedAt: Timestamp;
}

export class LivingPlanService {
  private get isE2EMode(): boolean {
    return isFirebaseE2EMockEnabled();
  }

  /**
   * Create a new living plan.
   */
  async create(
    userId: string,
    projectId: string,
    goal: string,
    initialDraft: PlanDraft,
  ): Promise<LivingPlan> {
    if (this.isE2EMode) {
      return {
        id: `e2e_plan_${Date.now()}`,
        projectId,
        userId,
        goal,
        draft: initialDraft,
        history: [],
        status: 'drafting',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
    }
    const now = serverTimestamp();
    const planData: Omit<LivingPlan, 'id'> = {
      projectId,
      userId,
      goal,
      draft: initialDraft,
      history: [],
      status: 'drafting',
      createdAt: now as Timestamp,
      updatedAt: now as Timestamp,
    };

    const docRef = await addDoc(
      collection(db, 'projects', projectId, 'livingPlans'),
      planData,
    );

    return {
      id: docRef.id,
      ...planData,
    };
  }

  /**
   * Retrieve a specific plan by ID.
   */
  async getPlan(projectId: string, planId: string): Promise<LivingPlan | null> {
    if (this.isE2EMode) {
      return null;
    }
    const docRef = doc(db, 'projects', projectId, 'livingPlans', planId);
    const snapshot = await getDoc(docRef);
    return snapshot.exists()
      ? ({ id: snapshot.id, ...snapshot.data() } as LivingPlan)
      : null;
  }

  /**
   * Alias for getPlan.
   */
  async get(projectId: string, planId: string): Promise<LivingPlan | null> {
    return this.getPlan(projectId, planId);
  }

  /**
   * List all plans for a project, optionally filtered by status.
   */
  async getPlansForProject(projectId: string, status?: PlanStatus): Promise<LivingPlan[]> {
    if (this.isE2EMode) {
      return [];
    }
    const q = status 
      ? query(
          collection(db, 'projects', projectId, 'livingPlans'),
          where('status', '==', status),
        )
      : query(
          collection(db, 'projects', projectId, 'livingPlans'),
        );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    } as LivingPlan));
  }

  /**
   * Update the status of a plan.
   */
  async updatePlanStatus(
    projectId: string,
    planId: string,
    status: PlanStatus
  ): Promise<void> {
    if (this.isE2EMode) return;
    const docRef = doc(db, 'projects', projectId, 'livingPlans', planId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp(),
      ...(status === 'executing' ? { approvedAt: serverTimestamp() } : {})
    });
  }

  /**
   * Update the draft of a plan.
   */
  async updateDraft(
    projectId: string,
    planId: string,
    draft: PlanDraft,
  ): Promise<void> {
    if (this.isE2EMode) return;
    const docRef = doc(db, 'projects', projectId, 'livingPlans', planId);
    await updateDoc(docRef, {
      draft,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Add a revision to the plan's history.
   */
  async addRevision(
    projectId: string,
    planId: string,
    revision: Omit<Revision, 'timestamp'>,
  ): Promise<void> {
    if (this.isE2EMode) return;
    const docRef = doc(db, 'projects', projectId, 'livingPlans', planId);
    const plan = await this.getPlan(projectId, planId);
    if (!plan) throw new Error('Plan not found');

    await updateDoc(docRef, {
      history: [
        ...plan.history,
        {
          ...revision,
          timestamp: serverTimestamp(),
        },
      ],
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Approve a plan and set it to executing.
   */
  async approve(
    projectId: string,
    planId: string,
  ): Promise<void> {
    return this.updatePlanStatus(projectId, planId, 'executing');
  }

  /**
   * Update the status of a specific step in the plan.
   */
  async updateStepStatus(
    projectId: string,
    planId: string,
    stepId: string,
    status: PlanStep['status'],
    error?: string,
    result?: unknown,
  ): Promise<void> {
    if (this.isE2EMode) return;
    const docRef = doc(db, 'projects', projectId, 'livingPlans', planId);
    const plan = await this.getPlan(projectId, planId);
    if (!plan || !plan.draft.steps) return;

    const newSteps = plan.draft.steps.map(step => {
      if (step.id === stepId) {
        return { ...step, status, error, result };
      }
      return step;
    });

    await updateDoc(docRef, {
      'draft.steps': newSteps,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Execute a plan by associating it with an execution reference.
   */
  async execute(
    projectId: string,
    planId: string,
    executionRef: LivingPlan['executionRef'],
  ): Promise<void> {
    if (this.isE2EMode) return;
    const docRef = doc(db, 'projects', projectId, 'livingPlans', planId);
    await updateDoc(docRef, {
      status: 'executing',
      executionRef,
      updatedAt: serverTimestamp(),
    });
  }

  /**
   * Orchestrate execution of plan steps via Promise.all for parallelism using A2A Swarm.
   *
   * @param runAgent The bound in-process agent runner (from AgentService). REQUIRED for
   *   the loopback A2A router to actually execute steps — without it the router has no
   *   way to invoke the target agent and every step fails with "No runAgent available".
   */
  async executePlanSteps(
    projectId: string,
    planId: string,
    runAgent: RouterCallContext['runAgent']
  ): Promise<void> {
    const plan = await this.getPlan(projectId, planId);
    if (!plan || !plan.draft.steps) return;

    const { a2aClient } = await importWithRetry(() => import('./a2a/A2AClient'));
    const steps = plan.draft.steps;
    const completed = new Set<string>();
    const pending = new Set(steps.map(s => s.id));
    const executing = new Set<string>();

    const executeStep = async (stepId: string) => {
      const step = steps.find(s => s.id === stepId);
      if (!step || !step.toolName) return;

      executing.add(stepId);
      await this.updateStepStatus(projectId, planId, stepId, 'executing');

      try {
        // toolName format is "agentId" or "agentId.something"; the leading segment
        // is the target agent. The A2A router only understands the JSON-RPC method
        // 'agent.execute' — the step's own task is what gets run on that agent.
        const agentId = step.toolName.split('.')[0] || 'generalist';

        // Construct a fully type-safe Directive object to satisfy A2AClient
        const stepDirective: Directive = {
          id: `dir_step_${step.id}`,
          userId: plan.userId,
          title: step.title,
          status: 'IN_PROGRESS',
          assignedAgent: agentId,
          goalAncestry: [
            {
              type: 'task',
              description: plan.goal,
              id: plan.id,
            }
          ],
          computeAllocation: {
            maxTokens: 50000,
            tokensUsed: 0,
            isMaximizerModeActive: false,
          },
          contextFiles: [],
          conversationThread: [],
          requiresDigitalHandshake: false,
          createdAt: Timestamp.now(),
          updatedAt: Timestamp.now(),
        };

        // Compose the task for the target agent from the step.
        const stepTask = [step.title, step.description].filter(Boolean).join(' — ');
        const result = await a2aClient.invoke(
          agentId,
          'agent.execute',
          { task: stepTask, sharedContext: JSON.stringify(step.input || {}) },
          stepDirective,
          { runAgent, traceId: `plan_${planId}_${step.id}` }
        );

        await this.updateStepStatus(projectId, planId, stepId, 'complete', undefined, result);
        completed.add(stepId);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : String(err);
        await this.updateStepStatus(projectId, planId, stepId, 'error', message);
      } finally {
        executing.delete(stepId);
        pending.delete(stepId);
      }
    };

    while (pending.size > 0) {
      const runnableSteps = Array.from(pending).filter(stepId => {
        const step = steps.find(s => s.id === stepId);
        if (executing.has(stepId)) return false;
        if (!step?.dependsOn || step.dependsOn.length === 0) return true;
        return step.dependsOn.every(dep => completed.has(dep));
      });

      if (runnableSteps.length === 0 && executing.size === 0) {
        // Deadlock or unresolvable dependencies
        break;
      }

      await Promise.all(runnableSteps.map(stepId => executeStep(stepId)));
    }
  }

  /**
   * Cancel a plan.
   */
  async cancel(
    projectId: string,
    planId: string,
  ): Promise<void> {
    return this.updatePlanStatus(projectId, planId, 'cancelled');
  }

  /**
   * Complete a plan.
   */
  async complete(
    projectId: string,
    planId: string,
  ): Promise<void> {
    return this.updatePlanStatus(projectId, planId, 'completed');
  }

  /**
   * List plans for a project that are currently in a "live" state.
   */
  async listByProject(projectId: string): Promise<LivingPlan[]> {
    if (this.isE2EMode) {
      return [];
    }
    const q = query(
      collection(db, 'projects', projectId, 'livingPlans'),
      where('status', 'in', ['drafting', 'awaiting_approval', 'executing', 'proposed']),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    } as LivingPlan));
  }

  /**
   * List plans awaiting approval.
   */
  async listAwaitingApproval(projectId: string): Promise<LivingPlan[]> {
    if (this.isE2EMode) {
      return [];
    }
    const q = query(
      collection(db, 'projects', projectId, 'livingPlans'),
      where('status', '==', 'awaiting_approval'),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
    } as LivingPlan));
  }
}

export const livingPlanService = new LivingPlanService();
